import { Launch } from "@carbon/icons-react";
import { TableToolbar, TableToolbarContent } from "@carbon/react";
import { IconButton, NullableTableColumns } from "@flaminem/ui-components";
import { toDate } from "@flaminem/utils";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AlertDecisionTag } from "src/components/Alerts/AlertDecisionTag";
import { AlertStatusTag } from "src/components/Alerts/AlertStatusTag";
import { FixedTable } from "src/components/Tables/FixedTable";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch";
import { useGetAlertDecisionLabel } from "src/hooks/alerts/useGetAlertDecisionLabel";
import { useGetAlertStatusLabel } from "src/hooks/alerts/useGetAlertStatusLabel";
import { useFormatDate, useT } from "src/internationalization";
import { getFullName } from "src/services/user/getUserFullName";
import { ShortAlert } from "src/types/alert";
import { searchInString } from "src/utils/string.utils";

export type AlertsHistoryTableProps = {
  alerts: ShortAlert[];
  isError?: boolean;
  isLoading?: boolean;
  openInNewTab?: boolean;
  caseNumber?: string;
  hideTitle?: boolean;
};

export const AlertsHistoryTable: FC<AlertsHistoryTableProps> = ({
  alerts,
  openInNewTab,
  caseNumber,
  isError = false,
  isLoading = false,
  hideTitle = false,
}) => {
  const t = useT();
  const formatDate = useFormatDate();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const searchRelatedAlert = useSearchRelatedAlert();

  const columns: NullableTableColumns<ShortAlert> = useMemo(
    () => ({
      date: {
        width: 1,
        flexGrow: 1,
        flexShrink: 1,
        label: t("alerts.history.table.columns.date"),
        render: (row) => {
          return row.adminStatus
            ? formatDate(toDate(row.adminStatus?.creation), {
                dateStyle: "medium",
              })
            : "";
        },
      },
      decision: {
        width: 1,
        flexGrow: 1,
        flexShrink: 1,
        label: t("alerts.history.table.columns.decision"),
        render: (row) => row.decision && <AlertDecisionTag decision={row.decision} />,
      },
      assignee: {
        width: 1,
        flexGrow: 1,
        flexShrink: 1,
        label: t("alerts.history.table.columns.assignee"),
        render: (row) => getFullName(row.assignee),
      },
      status: {
        width: 240,
        flexGrow: 0,
        flexShrink: 0,
        label: t("alerts.history.table.columns.status"),
        render: (row) => <AlertStatusTag status={row.status} />,
      },
      actions: openInNewTab
        ? {
            width: 64,
            flexGrow: 0,
            flexShrink: 0,
            label: "",
            render: (row) => (
              <IconButton
                kind="ghost-active"
                size="sm"
                label={t("alerts.history.table.openAlert.label")}
                onClick={() => {
                  window.open(`/alert/${row.id}`, "_blank");
                }}
              >
                <Launch width={16} height={16} />
              </IconButton>
            ),
          }
        : null,
    }),
    [openInNewTab, t, formatDate]
  );

  return (
    <FixedTable
      id="alerts-history-table"
      title={hideTitle ? undefined : t("alerts.history.table.title")}
      description={
        caseNumber && !hideTitle
          ? t("alerts.history.table.description", { number: caseNumber })
          : undefined
      }
      data={alerts ?? []}
      columns={columns}
      isSelectable={false}
      isLoading={isLoading}
      isError={isError}
      pageSize={10}
      onRowClick={openInNewTab ? undefined : (row) => navigate(`/alert/${row.id}`)}
      toolbar={() => (
        <TableToolbar aria-label="alerts history table toolbar">
          <TableToolbarContent>
            <TableToolbarSearch onSearchChange={setSearch} search={search} />
          </TableToolbarContent>
        </TableToolbar>
      )}
      filter={(row) => searchRelatedAlert(search, row)}
    />
  );
};

const useSearchRelatedAlert = () => {
  const getAlertDecisionLabel = useGetAlertDecisionLabel();
  const getAlertStatusLabel = useGetAlertStatusLabel();
  const formatDate = useFormatDate();
  const searchRelatedAlert = (search: string, alert: ShortAlert) => {
    if (!search) return true;

    return (
      searchInString(search, formatDate(alert.adminStatus.creation, { dateStyle: "medium" })) ||
      (alert.decision && searchInString(search, getAlertDecisionLabel(alert.decision))) ||
      (alert.assignee?.name && searchInString(search, getFullName(alert.assignee))) ||
      (alert.status && searchInString(search, getAlertStatusLabel(alert.status)))
    );
  };

  return searchRelatedAlert;
};
